import { useEffect, useRef, useState } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { Button, LoadingSpinner, InlineAlert } from '../components/BaseComponents'
import PageHeadings from '../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../components/BaseComponents/PageTemplate/PageTemplate'
import routes from '../components/Router/routes'
import { getAccountingPeriods } from '../redux/actions/accountingPeriodsActions'
import { getScope1EmissionTypes } from '../redux/actions/emissionTypesActions'
import { useAppDispatch, useAppSelector } from '../redux/hooks/reduxHooks'
import {
	ACCOUNTINGPERIODS_UPDATE_RESET,
	selectAccountingPeriods,
	selectAccountingPeriodsError,
	selectAccountingPeriodsStatus,
	selectAccountingPeriodsUpdateError,
	selectAccountingPeriodsUpdateStatus,
} from '../redux/slices/accountingPeriodsSlice'
import {
	selectAllScope1EmissionTypes,
	selectEmissionTypesError,
	selectEmissionTypesStatus,
} from '../redux/slices/emissionTypesSlice'
import { selectUpdateItemsError, selectUpdateItemsStatus, UPDATEITEMS_RESET } from '../redux/slices/updateItemsSlice'
import { FetchStatus, PostStatus } from '../types/LoadingStates'
import { updateItemsEmissionsType } from '../redux/actions/updateItemsActions'
import { centsToCurrency } from '../lib/utils'
import { SortConfig } from '../components/BaseComponents/Datagrid/useSortableData'
import { PurchaseUploadState } from '../enums/PurchaseUploadState'
import EmissionTypeInput from '../components/BaseComponents/Forms/EmissionTypeInput'
import {
	GET_PURCHASES_CARBON_ACCOUNTS_LIMIT,
} from '../redux/actions/carbonAccountsPurchasesActions'
import SimpleSortTable, {
	TableColumn,
} from '../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import Pagination from '../components/BaseComponents/Pagination/Pagination'
import { Scope1EmissionItem } from '../types/PurchaseCarbonData'
import { selectRefinementsScope1EmissionError, selectRefinementsScope1EmissionItems, selectRefinementsScope1EmissionStatus } from '../redux/slices/refinementsSlice'
import { PaginationMeta } from '../types/PaginationMeta'
import { getScope1EmissionItems } from '../redux/actions/refinementsActions'
import useCurrencyFormatter from '../hooks/useCurrencyFormatter'
import useApi from '../hooks/useApi'
import useCompleteAccountingStage from '../hooks/useCompleteAccountingStage'
import useRefinementRequest from '../hooks/useRefinementRequest'
import useInterruptibleRefinementSave from '../hooks/useInterruptibleRefinementSave'
import NumericText from '../components/BaseComponents/NumericText/NumericText'

const CompanyRefineEmissionsType = () => {
	const formatCurrency = useCurrencyFormatter();
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const api = useApi();

	const accountingPeriodsStatus = useAppSelector(selectAccountingPeriodsStatus)
	const accountingPeriodsError = useAppSelector(selectAccountingPeriodsError)
	const accountingPeriodUpdateStatus = useAppSelector(selectAccountingPeriodsUpdateStatus)
	const accountingPeriodUpdateError = useAppSelector(selectAccountingPeriodsUpdateError)
	const allAccountingPeriodData = useAppSelector(selectAccountingPeriods)
	const [errorNoPeriod, setErrorNoPeriod] = useState(false)

	const emissionTypesStatus = useAppSelector(selectEmissionTypesStatus)
	const emissionTypesError = useAppSelector(selectEmissionTypesError)
	const allScope1EmissionTypes = useAppSelector(selectAllScope1EmissionTypes)

	const itemLoadStatus = useAppSelector(selectRefinementsScope1EmissionStatus)
	const itemError = useAppSelector(selectRefinementsScope1EmissionError)
	const itemData = useAppSelector(selectRefinementsScope1EmissionItems)
	const defaultPagination: PaginationMeta = {
		current_page: 1,
		first_page: 1,
		last_page: 1,
		per_page: 20,
		total: 0,
	}
	const { items, pagination } = itemData || { items: [], pagination: defaultPagination }

	const limit = GET_PURCHASES_CARBON_ACCOUNTS_LIMIT
	const [orderBy, setOrderBy] = useState<string>('itemName')
	const [direction, setDirection] = useState<'asc' | 'desc'>('asc')
	const { accountingPeriodId } = useParams() as { accountingPeriodId: string }

	const [dirtyItems, setDirtyItems] = useState<{ itemId: string, emissionTypeId: string|null}[]>([])

	const updateItemsStatus = useAppSelector(selectUpdateItemsStatus)
	const updateItemsError = useAppSelector(selectUpdateItemsError)

	// Find the accounting period to match the url param
	const thisAccountingPeriod = allAccountingPeriodData.filter(
		(period) => period.id === accountingPeriodId
	)[0]
	const request = useRefinementRequest(api, accountingPeriodId)
	const typeRequest = useRefinementRequest(api, accountingPeriodId)
	const startSave = useInterruptibleRefinementSave(api, accountingPeriodId)
	const lastQuery = useRef({ page: 1, orderBy: 'itemName', direction: 'asc' as 'asc' | 'desc' })
	const loadPurchases = (query: typeof lastQuery.current) => {
		lastQuery.current = query
		request.start((signal) => dispatch(getScope1EmissionItems(
			api, accountingPeriodId, query.page, query.orderBy, query.direction, limit, { signal }
		)))
	}
	const loadEmissionTypes = () => typeRequest.start((signal) => dispatch(getScope1EmissionTypes(api, undefined, { signal })))
	const completeStage = useCompleteAccountingStage(PurchaseUploadState.scope1Refined, () => {
		dispatch(UPDATEITEMS_RESET())
		dispatch(ACCOUNTINGPERIODS_UPDATE_RESET())
		navigate(generatePath(routes.companyRefineElectricity.path, { accountingPeriodId }))
	})
	const canContinue = request.current && typeRequest.current && Boolean(thisAccountingPeriod) &&
		accountingPeriodsStatus === FetchStatus.loaded && itemLoadStatus === FetchStatus.loaded &&
		emissionTypesStatus === FetchStatus.loaded && updateItemsStatus !== PostStatus.sending &&
		updateItemsStatus !== PostStatus.sent && accountingPeriodUpdateStatus !== PostStatus.sending &&
		accountingPeriodUpdateStatus !== PostStatus.error
	const saveSelection = () => {
		const { signal, onSubmitted, finish } = startSave()
		const save = async () => {
			const saved = await dispatch(updateItemsEmissionsType(api, dirtyItems.map((item) => ({
				id: item.itemId,
				emission_type: item.emissionTypeId,
			})), { signal, onSubmitted }))
			finish()
			if (!signal.aborted && saved) completeStage(accountingPeriodId)
		}
		save()
	}

	useEffect(() => {
		dispatch(ACCOUNTINGPERIODS_UPDATE_RESET())
		dispatch(UPDATEITEMS_RESET())
		setDirtyItems([])
	}, [dispatch, api, accountingPeriodId])

	useEffect(() => {
		loadEmissionTypes()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [api, accountingPeriodId])

	// Data loading
	useEffect(() => {
		// Fetch accounting periods if they aren't in the store, we need to compare against the IDs
		if (accountingPeriodsStatus === FetchStatus.initial) {
			dispatch(getAccountingPeriods(api))
		}

		// Refetch purchases (regardless) as we need updated scope information
		if (accountingPeriodsStatus === FetchStatus.loaded) {
			if (thisAccountingPeriod) loadPurchases({ page: 1, orderBy, direction })
			setErrorNoPeriod(typeof thisAccountingPeriod === 'undefined')
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountingPeriodsStatus, api, accountingPeriodId])

	// DataGrid Columns
	const columns: TableColumn[] = [
		{ id: 'itemName', label: 'Product', sortable: true },
		{ id: 'supplierName', label: 'Supplier', sortable: true },
		{
			id: 'totalQuantity',
			label: 'Quantity',
			sortable: true,
			renderer: (columnKey, rowData) => {
				return (
					<>
						<p className=' text-white '>
							<NumericText value={rowData.totalQuantity} /> {rowData.quantityUnit}
						</p>
						<p className=' text-charcoal-100 '>(<NumericText value={formatCurrency(rowData.price)} />)</p>
					</>
				)
			},
		},
		{
			id: 'emissionType',
			label: 'Emission Type',
			renderer: (columnKey, rowData) => {
				const dirtyItem = dirtyItems.find((item) => item.itemId === rowData._id)
				return (
					<div>
						{emissionTypesStatus === FetchStatus.loaded && (
							<EmissionTypeInput
								emissionTypeData={allScope1EmissionTypes}
								initialValue={dirtyItem ? dirtyItem.emissionTypeId : rowData.emissionTypeId}
								isDisabled={!canContinue}
								onChange={(emissionId) => {
									const changes = dirtyItems.filter((p) => p.itemId !== rowData._id)
									const itemChange = {
										itemId: rowData._id,
										emissionTypeId: emissionId || null,
									}
									setDirtyItems([
										...changes,
										itemChange
									])
								}}
							/>
						)}
					</div>
				)
			},
		},
	]
	const defaultSorting: SortConfig = {
		key: 'itemName',
		direction: 'ascending',
	}

	// DataGrid data constructor
	const constructData = (dataSet: Scope1EmissionItem[]) => dataSet.map((item) => {
		return {
			_id: item.itemId,
			itemName: item.itemName,
			price: centsToCurrency(item.totalSpend),
			supplierName: item.supplierName,
			totalQuantity: item.totalQuantity,
			quantityUnit: item.quantityUnit,
			emissionTypeId: item.emissionTypeId
		}
	})
	return (
		<PageTemplate>
			<Helmet>
				<title>{routes.companyRefineEmissionsGeneratedType.name}</title>
			</Helmet>
			<PageHeadings
				crumbs={[
					{ label: 'Purchases', route: routes.companyPurchases.path },
					{ label: routes.companyAddAccountingPeriods.menuName as string, route: routes.companyAddAccountingPeriods.path },
					{ label: 'Refine scope 1 data - Emissions generated', route: generatePath(routes.companyRefineEmissionsGenerated.path, { accountingPeriodId }) },
					{ label: 'Emissions generated type' },
				]}
			/>

			<>
				<div className='rounded-lg bg-sun-500 p-4 mt-5'>
					<p>Select the emission type for the purchases that generate emissions.</p>
				</div>
				{request.current && typeRequest.current && accountingPeriodsStatus !== FetchStatus.error && itemLoadStatus !== FetchStatus.error &&
					emissionTypesStatus !== FetchStatus.error && <><SimpleSortTable
					isLoading={
						accountingPeriodsStatus === FetchStatus.loading ||
						itemLoadStatus === FetchStatus.loading ||
						emissionTypesStatus === FetchStatus.loading
					}
					additionalClass='mt-4'
					data={constructData(items)}
					columns={columns}
					defaultSorting={defaultSorting}
					onSortPress={(key, direction) => {
						// Store current sorting values for pagination to reference in its API calls
						setOrderBy(key)
						setDirection(direction === 'ascending' ? 'asc' : 'desc')
						loadPurchases({ page: 1, orderBy: key, direction: direction === 'ascending' ? 'asc' : 'desc' })
					}}
				/>
				<Pagination
					isLoading={
						accountingPeriodsStatus === FetchStatus.loading ||
						itemLoadStatus === FetchStatus.loading
					}
					pagination={pagination}
					onPageChange={(newPageNumber) => {
						loadPurchases({ page: newPageNumber, orderBy, direction })
					}}
				/></>}

				<div className='rounded-lg bg-charcoal-600 p-2 mt-5'>
					<Button
						type={'Primary'}
						text='Next Step'
						icon='fa-solid fa-chevron-right'
						additionalClass='float-right'
						hasTrailingIcon
						disabled={!canContinue}
						onClick={saveSelection}
					/>
				</div>
			</>

			{/* Errors and loading states*/}
			{accountingPeriodsStatus === FetchStatus.loading && (
				<LoadingSpinner loadingText='Loading accounting periods...' />
			)}
			{(updateItemsStatus === PostStatus.sending || accountingPeriodUpdateStatus === PostStatus.sending) && (
				<LoadingSpinner loadingText='Sending items...' />
			)}
			{accountingPeriodsStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching the accounting periods list. If this problem persists,
						please contact the Neoni team.
					</p>
					<p className='text-white'>{accountingPeriodsError}</p>
					<Button type='Primary' text='Retry loading accounting periods' onClick={() => dispatch(getAccountingPeriods(api))} />
				</InlineAlert>
			)}
			{request.current && itemLoadStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching the purchases for this accounting period (
						{accountingPeriodId}). If this problem persists, please contact the Neoni team.
					</p>
					<p className='text-white'>{itemError}</p>
					<Button type='Primary' text='Retry loading purchases' onClick={() => loadPurchases(lastQuery.current)} />
				</InlineAlert>
			)}
			{typeRequest.current && emissionTypesStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>
					<p className='text-white'>Could not load emission types.</p>
					<p className='text-white'>{emissionTypesError}</p>
					<Button type='Primary' text='Retry loading emission types' onClick={loadEmissionTypes} />
				</InlineAlert>
			)}
			{accountingPeriodUpdateStatus === PostStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						Your selections were saved, but the refinement stage could not be completed.
					</p>
					<p className='text-white'>{accountingPeriodUpdateError}</p>
					<Button type='Primary' text='Retry completion' onClick={() => completeStage(accountingPeriodId)} />
				</InlineAlert>
			)}
			{updateItemsStatus === PostStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error updating those items' emission types. If this problem persists,
						please contact the Neoni team.
					</p>
				<p className='text-white'>{updateItemsError}</p>
				<Button type='Primary' text='Retry updating emission types' disabled={!canContinue} onClick={saveSelection} />
				</InlineAlert>
			)}
			{errorNoPeriod && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error whilst trying to find purchases for that accounting period ID (
						{accountingPeriodId}) in scope 1. If this problem persists, please contact the Neoni
						team.
					</p>
					<Button
						onClick={() => {
							navigate(generatePath(routes.companyRefineEmissionsGeneratedType.path, {accountingPeriodId}), {
								replace: true,
							})
						}}
						type='Primary'
						hasTrailingIcon={true}
						icon='fa-solid fa-rotate-right'
						text='Try again'
						additionalClass='mt-9 mr-5'
					/>
					<Button
						type='Outlined'
						text='Back to accounting periods'
						onClick={() => {
							navigate(routes.companyAddAccountingPeriods.path)
						}}
						additionalClass='mt-6 mr-5'
					/>
				</InlineAlert>
			)}
		</PageTemplate>
	)
}

export default CompanyRefineEmissionsType
